import React, { useState } from "react";
import styles from "./Contact.module.css";
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaPrint } from "react-icons/fa";
import SEO from "../components/SEO";
import TurnstileWidget, { TURNSTILE_ACTION } from "../components/TurnstileWidget";
import ObfuscatedEmail from "../components/ObfuscatedEmail";

const Contact = () => {
    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        service: "",
        message: "",
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState({ type: "", message: "" });
    const [turnstileToken, setTurnstileToken] = useState("");
    const [turnstileResetSignal, setTurnstileResetSignal] = useState(0);

    const resetTurnstile = () => {
        setTurnstileToken("");
        setTurnstileResetSignal((value) => value + 1);
    };

    const getErrorMessage = (data, fallback) => {
        if (data.errors && data.errors.length > 0) {
            const firstError = data.errors[0];
            return typeof firstError === "string" ? firstError : firstError.msg;
        }

        return data.message || fallback;
    };

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
        // Clear status message when user starts typing
        if (submitStatus.message) {
            setSubmitStatus({ type: "", message: "" });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!turnstileToken) {
            setSubmitStatus({
                type: "error",
                message: "Please complete the security check before sending your message.",
            });
            return;
        }

        setIsSubmitting(true);
        setSubmitStatus({ type: "", message: "" });

        try {
            // Prepare data for PHP backend
            const submitData = {
                name: `${formData.firstName} ${formData.lastName}`.trim(),
                email: formData.email,
                phone: formData.phone,
                message: formData.service ? `Subject: ${formData.service}\n\n${formData.message}` : formData.message,
                turnstileToken,
                turnstileAction: TURNSTILE_ACTION,
            };

            const response = await fetch("/api/contact.php", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(submitData),
            });

            const data = await response.json();

            if (response.ok && data.success) {
                setSubmitStatus({
                    type: "success",
                    message: "Thank you for your message! We will get back to you within 24 hours.",
                });
                // Reset form
                setFormData({
                    firstName: "",
                    lastName: "",
                    email: "",
                    phone: "",
                    service: "",
                    message: "",
                });
            } else {
                setSubmitStatus({
                    type: "error",
                    message: getErrorMessage(data, "Failed to send message. Please try again."),
                });
            }
        } catch (error) {
            console.error("Error submitting form:", error);
            setSubmitStatus({
                type: "error",
                message: "Unable to connect to server. Please try again or call us at (818) 949-1031.",
            });
        } finally {
            resetTurnstile();
            setIsSubmitting(false);
        }
    };

    return (
        <div className={styles.contactPage}>
            <SEO
                title="Contact Us"
                description="Contact All For You Home Health Care in Pasadena, CA. Call (818) 949-1031 or visit us at 40 N. Altadena Dr. Ste 200J. Monday-Friday, 10 AM-4 PM. Free consultations available."
                keywords="contact, home health care Pasadena, Pasadena home care, 40 N Altadena Dr, phone (818) 949-1031, free consultation"
                canonicalUrl="https://allforyouhc.com/contact"
            />
            {/* Hero Section with Form and Info */}
            <section className={styles.heroSection}>
                <div className={styles.container}>
                    <div className={styles.sectionHeader}>
                        <h1>Get in touch</h1>
                    </div>

                    <div className={styles.heroGrid}>
                        {/* Left Side - Form */}
                        <div className={styles.formContainer}>
                            <form onSubmit={handleSubmit} className={styles.contactForm}>
                                <div className={styles.formRow}>
                                    <div className={styles.formGroup}>
                                        <label htmlFor="firstName">Name</label>
                                        <input
                                            type="text"
                                            id="firstName"
                                            name="firstName"
                                            placeholder="John Carter"
                                            value={formData.firstName}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>

                                    <div className={styles.formGroup}>
                                        <label htmlFor="email">Email</label>
                                        <input
                                            type="email"
                                            id="email"
                                            name="email"
                                            placeholder="example@email.com"
                                            value={formData.email}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                </div>

                                <div className={styles.formRow}>
                                    <div className={styles.formGroup}>
                                        <label htmlFor="phone">Phone</label>
                                        <input
                                            type="tel"
                                            id="phone"
                                            name="phone"
                                            placeholder="(123) 456 - 789"
                                            value={formData.phone}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>

                                    <div className={styles.formGroup}>
                                        <label htmlFor="service">Subject</label>
                                        <input
                                            type="text"
                                            id="service"
                                            name="service"
                                            placeholder="Subject"
                                            value={formData.service}
                                            onChange={handleChange}
                                        />
                                    </div>
                                </div>

                                <div className={styles.formGroup}>
                                    <label htmlFor="message">Message</label>
                                    <textarea
                                        id="message"
                                        name="message"
                                        rows="4"
                                        placeholder="Please type your message here..."
                                        value={formData.message}
                                        onChange={handleChange}
                                        required
                                    ></textarea>
                                </div>

                                {submitStatus.message && (
                                    <div className={`${styles.statusMessage} ${styles[submitStatus.type]}`}>{submitStatus.message}</div>
                                )}

                                <div className={styles.turnstileField}>
                                    <TurnstileWidget
                                        resetSignal={turnstileResetSignal}
                                        onVerify={setTurnstileToken}
                                        onExpire={() => setTurnstileToken("")}
                                        onError={() => setTurnstileToken("")}
                                    />
                                </div>

                                <button type="submit" className={styles.submitBtn} disabled={isSubmitting || !turnstileToken}>
                                    {isSubmitting ? "Sending..." : "Send Message"}
                                </button>
                            </form>
                        </div>

                        {/* Right Side - Info */}
                        <div className={styles.infoContainer}>
                            <p className={styles.infoDescription}>
                                We're here to answer any questions you may have about our home health care services. Reach out to us and we'll respond
                                within 24 hours.
                            </p>

                            <div className={styles.infoCards}>
                                <div className={styles.infoCard}>
                                    <div className={styles.infoIcon}>
                                        <FaEnvelope />
                                    </div>
                                    <div className={styles.infoContent}>
                                        <h4>Email</h4>
                                        <ObfuscatedEmail />
                                    </div>
                                </div>

                                <div className={styles.infoCard}>
                                    <div className={styles.infoIcon}>
                                        <FaPhone />
                                    </div>
                                    <div className={styles.phoneNumbers}>
                                        <div className={styles.phoneItem}>
                                            <h4>Office</h4>
                                            <a href="tel:8189391031">(818) 939-1031</a>
                                        </div>
                                        <div className={styles.phoneItem}>
                                            <h4>Phone</h4>
                                            <a href="tel:7604242000">(760) 424-2000</a>
                                        </div>
                                    </div>
                                </div>

                                <div className={styles.infoCard}>
                                    <div className={styles.infoIcon}>
                                        <FaPrint />
                                    </div>
                                    <div className={styles.infoContent}>
                                        <h4>Fax</h4>
                                        <span>(818) 949-1032</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Location Section */}
            <section className={styles.locationSection}>
                <div className={styles.container}>
                    <div className={styles.locationHeader}>
                        <h2>Come and visit us!</h2>
                        <p>Visit us during office hours: Monday - Friday, 10:00 AM - 4:00 PM</p>
                    </div>

                    <div className={styles.locationCard}>
                        <div className={styles.locationImage}>
                            <iframe
                                title="Office Location"
                                src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d4136.093495325945!2d-118.1006836!3d34.1464537!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80c2ddf86c671e45%3A0x25b70e7d9342d39f!2sAll%20For%20You%20Home%20Health%20Care!5e1!3m2!1sen!2sus!4v1768997196829!5m2!1sen!2sus"
                                width="100%"
                                height="100%"
                                style={{ border: 0 }}
                                allowFullScreen=""
                                loading="lazy"
                            ></iframe>
                        </div>
                        <div className={styles.locationInfo}>
                            <h3>40 N. Altadena Dr. Ste 200J</h3>
                            <h4>Pasadena, CA 91107</h4>
                            {/* <p>Our main office location serving Los Angeles, San Bernardino, Orange, Ventura, and Riverside counties.</p> */}
                            <br />
                            <br />
                            <div className={styles.locationContact}>
                                <div className={styles.contactItem}>
                                    <FaMapMarkerAlt />
                                    <a href="https://maps.app.goo.gl/MP6fUPqp6gZdupjt8" target="_blank" rel="noopener noreferrer">
                                        40 N. Altadena Dr. Ste 200J, Pasadena, CA 91107
                                    </a>
                                </div>
                                <div className={styles.contactItem}>
                                    <FaPhone />
                                    <a href="tel:8189491031">(818) 949-1031</a>
                                </div>
                                <div className={styles.contactItem}>
                                    <FaPhone />
                                    <a href="tel:7604242000">(760) 424-2000</a>
                                </div>
                                <div className={styles.contactItem}>
                                    <FaPrint />
                                    <span>Fax: (818) 949-1032</span>
                                </div>
                                <div className={styles.contactItem}>
                                    <FaEnvelope />
                                    <ObfuscatedEmail />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Contact;
